import React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import useCountdown from 'hooks/useCountdown';
import useScrollRef from 'hooks/useScrollRef';
import { ButtonType } from 'components/ui/Button';
import RadioBullet from 'components/ui/RadioBullet';
import { ChannelEnum, AdditionalChannelEnum, UIChannelEnum } from 'modules/auth/types/LoginRequest';
import {
  Button, Icon, Modal,
} from 'components/ui';
import { getAvailableChannels,
  getDefaultChannelToSendCode,
  getDefaultUIChannelToSendCode,
  getMFAPayload,
  getRetryExpiration,
  normalizeChannel } from 'modules/communication/utils';
import { CommunicationState } from '../../modules/communication/types/CommunicationState';
import { CustomerType } from 'modules/selfServices/types/SelfService';


interface SelectCommunicationChannelModalProps {
  onSubmit: (channel: ChannelEnum, customerType?: CustomerType) => void | Promise<void>;
  onClose: (value: boolean) => void;
  blackListChannels?: UIChannelEnum[];
  maxAttemptReached?: boolean;
  title: MessageDescriptor;
  message?: MessageDescriptor;
  sendToChannelMessage: MessageDescriptor;
  withCountdown?: boolean;
  data: CommunicationState;
}

const USED_FIELD_BY_CHANNEL = {
  [ChannelEnum.EMAIL]: 'email',
  [ChannelEnum.SMS]: 'phoneNumber',
  [ChannelEnum.WHATSAPP]: 'phoneNumber',
  [AdditionalChannelEnum.EMAIL_ADDITIONAL]: 'additionalCustomerEmail',
  [AdditionalChannelEnum.SMS_ADDITIONAL]: 'additionalCustomerPhoneNumber',
  [AdditionalChannelEnum.WHATSAPP_ADDITIONAL]: 'additionalCustomerPhoneNumber',
} as const satisfies Record<UIChannelEnum, keyof CommunicationState>;

const SelectCommunicationChannelModal: React.FC<SelectCommunicationChannelModalProps> = ({
  onSubmit,
  onClose,
  blackListChannels,
  maxAttemptReached,
  sendToChannelMessage,
  title,
  message,
  withCountdown,
  data,
}) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = React.useState(false);
  const { canScroll, handleScroll } = useScrollRef(scrollRef);

  const [channel, setChannel] = React.useState<UIChannelEnum>(getDefaultUIChannelToSendCode(data, blackListChannels));

  const { countdown: timeRemaining } = useCountdown({ date: getRetryExpiration(data, channel) });

  const availableChannels = React.useMemo(() => getAvailableChannels(data), [data]);
  const isDisabled = isLoading || (withCountdown && timeRemaining > 0) || maxAttemptReached;

  const handleClose = React.useCallback(() => onClose(false), [onClose]);

  const handleSubmit = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const { value, customerType } = getMFAPayload(channel);
      await onSubmit(value, customerType);
      handleClose();
    } catch {
      // do nothing, modal stays opened
    }
    setIsLoading(false);
  }, [onSubmit, channel, handleClose]);

  const handleChange = (value: UIChannelEnum) => {
    setChannel(value);
  };

  return (
    <Modal
      open
      focusRef={ref}
      className="modal-large"
      onClose={onClose}
      title={<FormattedMessage tagName="h2" {...title} />}
    >
      <div className="flex flex-col items-center mt-5" data-testid="resendModal">
        {message && (
          <p className="mb-3xs">
            <FormattedMessage {...message} values={{ channel: getDefaultChannelToSendCode(data) }} />
          </p>
        )}
        <div className="relative w-full">
          <div
            ref={scrollRef}
            className="w-full overflow-y-auto max-h-[35vh]"
          >
            {availableChannels.map((availableChannel: UIChannelEnum) => {
              const traductionKey = normalizeChannel(availableChannel).toLowerCase();
              const isAdditionalCustomerEntry = traductionKey !== availableChannel.toLowerCase();
              const channelLower = availableChannel.toLowerCase();
              return (
                <div className="w-full" key={availableChannel}>
                  <RadioBullet
                    name="channel"
                    value={availableChannel}
                    checked={channel === availableChannel}
                    onChange={handleChange}
                    data-testid={`${channelLower}-radio`}
                    title={(
                      <FormattedMessage
                        id={`login.resend.${traductionKey}.title`}
                        defaultMessage={`${availableChannel}{isAdditionalCustomerEntry, select, true { - additional contact} other {}}`}
                        values={{ isAdditionalCustomerEntry: String(isAdditionalCustomerEntry) }}
                      />
                    )}
                    description={(
                      <FormattedMessage
                        id={`login.resend.${traductionKey}.description`}
                        defaultMessage={`${availableChannel} sent to {value}`}
                        values={{ value: data[USED_FIELD_BY_CHANNEL[availableChannel]] }}
                      />
                    )}
                  />
                </div>
              );
            })}
          </div>
          {canScroll && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
              <Button
                type={ButtonType.SECONDARY}
                onClick={handleScroll}
                className="rounded-full! p-3! shadow-md flex"
              >
                <Icon className="fb-icon--arrow-down text-icon" />
              </Button>
            </div>
          )}
        </div>
        {maxAttemptReached && (
          <span className="text-center mb-4">
            <FormattedMessage
              id="login.resend.reachedMaxAttempts"
              defaultMessage="You reached the maximum number of attempts."
            />
          </span>
        )}
        {!maxAttemptReached && (
          <Button
            ref={ref}
            loading={isLoading}
            onClick={handleSubmit}
            className="w-full mt-3xs mb-2xs"
            disabled={isDisabled}
            testId="sendToChannel"
          >
            <FormattedMessage {...sendToChannelMessage} values={{ countdown: timeRemaining, channel }} />
          </Button>
        )}
        <Button onClick={handleClose} type={ButtonType.SECONDARY} className="w-full" testId="cancel-button">
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
      </div>
    </Modal>
  );
};

export default SelectCommunicationChannelModal;
